// CabinetVisioPage — visio Jitsi réelle, salle UNIQUE par rendez-vous (v5.19.0).
// /cabinet/:slug/visio/:apptId — infos du RDV avant l'entrée · états scheduled→waiting→in_progress.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Video } from "lucide-react";
import { toast } from "sonner";
import { T, Avatar } from "@/components/cabinet/cabinetTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem("civ_user_token") || localStorage.getItem("aya_founder_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CabinetVisioPage() {
  const { slug, apptId } = useParams();
  const navigate = useNavigate();
  const [apt, setApt] = useState(null);
  const [err, setErr] = useState(null);
  const [room, setRoom] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/v3/cabinet/appointments/${apptId}`, { headers: authHeaders() })
      .then((r) => setApt(r.data.appointment))
      .catch((e) => setErr(e.response?.status === 401 ? "auth" : "notfound"));
  }, [apptId]);

  const join = async () => {
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/appointments/${apptId}/join`, {}, { headers: authHeaders() });
      setRoom(r.data.jitsi_room);
    } catch (e) {
      toast.error(e.response?.data?.error === "appointment_closed" ? "Ce rendez-vous est terminé." : "Impossible de rejoindre la visio");
    }
  };

  if (err === "auth") return (
    <div style={{ ...T.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
      <div style={T.sub}>Connectez-vous pour accéder à cette visio.</div>
      <button style={T.btnPrimary} onClick={() => navigate(`/login?next=/cabinet/${slug}/visio/${apptId}`)}>Se connecter</button>
    </div>
  );
  if (err) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#5F6F66" }}>Rendez-vous introuvable.</div>;
  if (!apt) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#5F6F66" }}>Chargement…</div>;

  // Salle active : plein écran Jitsi (vraie visioconférence, salle non devinable).
  if (room) return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#0B1F14" }} data-testid="visio-room">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", color: "#fff" }}>
        <span style={{ fontSize: 13, fontWeight: 800 }}>📹 {apt.member?.name} · {apt.time}</span>
        <button onClick={() => setRoom(null)} style={{ ...T.back, padding: "6px 12px" }} data-testid="visio-leave">Quitter</button>
      </div>
      <iframe
        title="Visioconférence AYA Cabinet"
        src={`https://meet.jit.si/${encodeURIComponent(room)}#config.prejoinConfig.enabled=false&userInfo.displayName=${encodeURIComponent(apt.client_name || "Participant AYA")}`}
        style={{ flex: 1, border: "none", width: "100%" }}
        allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write; screen-wake-lock"
        data-testid="visio-iframe"
      />
    </div>
  );

  return (
    <div style={T.page} data-testid="visio-page">
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "16px", display: "grid", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ ...T.back, width: "fit-content" }} data-testid="visio-back"><ArrowLeft size={14} /> Retour</button>
        <div style={{ ...T.card, textAlign: "center", padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><Avatar name={apt.member?.name} color={apt.member?.color} size={62} /></div>
          <h1 style={{ ...T.h1, fontSize: 19 }} data-testid="visio-member-name">{apt.member?.name}</h1>
          <div style={T.sub}>{apt.member?.title} · {apt.cabinet?.name}</div>
          <div style={{ ...T.card, background: "#F6FAF7", margin: "14px 0", textAlign: "left", fontSize: 13, lineHeight: 1.8 }}>
            📅 <b>{apt.date}</b> à <b>{apt.time}</b> · ⏱ {apt.duration_min} min<br />
            Type : {apt.type}<br />
            Statut : <span style={{ ...T.badge, ...T.badgeGreen }} data-testid="visio-status">{apt.status}</span>
          </div>
          {apt.mode === "visio" ? (
            <button style={{ ...T.btnPrimary, width: "100%", padding: "14px 0" }} onClick={join} data-testid="visio-join-btn">
              <Video size={17} /> Rejoindre la visio
            </button>
          ) : (
            <div style={T.sub}>Ce rendez-vous a lieu au cabinet — {apt.cabinet?.name}.</div>
          )}
        </div>
      </div>
    </div>
  );
}
